import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { clearMethodsCache } from "../lib/methodsCache";

const API = process.env.REACT_APP_BACKEND_URL;

// 2026-02-27 (Dave · perf C) — Boot prefetch cache for /api/associations.
// AuthContext fires the fetch in parallel with /auth/me and /api/tenants
// during login/refresh, and stashes the in-flight Promise here. Shell's
// loadAssociations() (in App.js) awaits this Promise instead of firing
// its own duplicate request — saves one round-trip off cold login. The
// cache is cleared on logout so a subsequent sign-in re-fetches fresh.
export const associationsPrefetch = { promise: null, at: 0 };
function primeAssociationsPrefetch(token) {
  associationsPrefetch.promise = axios
    .get(`${API}/api/associations`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    .then((res) => res.data)
    .catch(() => null); // Non-fatal — Shell will retry with its own call.
  associationsPrefetch.at = Date.now();
}

// Auth state shape:
//   user:      null (unknown, still loading), false (logged out), or user object
//   memberships[] comes with the user object
const AuthCtx = createContext(null);

// -------------------------------------------------------------------------
// Dual-track auth (Dave 2026-02-20):
//   The backend sets an httpOnly cookie AND returns `access_token`. Some
//   preview embeds (emergent.sh iframe) block third-party cookies which was
//   silently logging users out on every reload. We now ALSO keep the raw
//   JWT in localStorage and send it as `Authorization: Bearer …` on every
//   request. Whichever channel the browser accepts, the session survives.
// -------------------------------------------------------------------------
const TOKEN_KEY = "ureserve.token";

// Step 7.25 (2026-08-02) — When "Keep me logged in" is checked at login we
// persist in localStorage (survives tab close). When unchecked we use
// sessionStorage (dies on tab close, session-only). readToken() checks
// both so an existing session isn't lost between refreshes.
function readToken() {
  try {
    return (
      window.localStorage.getItem(TOKEN_KEY) ||
      window.sessionStorage.getItem(TOKEN_KEY) ||
      ""
    );
  } catch { return ""; }
}

function writeToken(t, persistent = true) {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(TOKEN_KEY);
    if (t) {
      const store = persistent ? window.localStorage : window.sessionStorage;
      store.setItem(TOKEN_KEY, t);
    }
  } catch { /* ignore */ }
}

// Apply/remove the Authorization header on the shared axios instance. All
// screens import the same `axios`, so this covers every existing call site
// without touching them individually.
function applyAxiosAuth(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
  axios.defaults.withCredentials = true;   // still send the cookie when allowed
}

// Bootstrap header from any persisted token so the very first request on
// page-load already carries auth (before AuthProvider mounts).
applyAxiosAuth(readToken());

// UX-only read of the JWT `exp` claim (the server still verifies every request).
// To tolerate a skewed device clock, a freshly issued token's expiry is pinned
// to local time as now + (exp - iat) and remembered for later page loads.
const EXP_LOCAL_KEY = "ureserve.tokenExpLocal";
function decodeClaims(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(part));
  } catch { return null; }
}
export function tokenExpiryMs(token, freshlyIssued = false) {
  const c = token && decodeClaims(token);
  if (!c || typeof c.exp !== "number") return null;
  const sig = token.slice(-16);
  try {
    if (freshlyIssued && typeof c.iat === "number") {
      const at = Date.now() + (c.exp - c.iat) * 1000;
      window.localStorage.setItem(EXP_LOCAL_KEY, JSON.stringify({ sig, at }));
      return at;
    }
    const saved = JSON.parse(window.localStorage.getItem(EXP_LOCAL_KEY) || "null");
    if (saved && saved.sig === sig) return saved.at;
  } catch { /* storage blocked */ }
  return c.exp * 1000;
}

export const SESSION_EXPIRED_MSG = "Your session has expired — please sign in again.";
// Server 401 details that mean "this sign-in is no longer valid".
const DEAD_SESSION_DETAILS = new Set([
  "Token expired",
  "Invalid token",
  "User not found or inactive",
  "Session expired — please sign in again",
]);

// Small wrapper around fetch that mirrors the axios behaviour above — used
// by AuthContext's own calls to /api/auth/*. Also exported for other
// contexts (e.g. SettingsContext) that need a token-aware fetch.
export function authFetch(url, opts = {}) {
  const token = readToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { credentials: "include", ...opts, headers });
}

// 2026-02-15 (Dave) — True when this device holds a PERSISTENT session
// (login used "Keep me logged in"). Signal is which storage tier has the
// token: localStorage → remembered device; sessionStorage → ephemeral.
// AppGate uses this to disarm the idle-logout timer for remembered
// devices so the 30-minute idle default doesn't defeat the 14-day
// remember-me contract.
export function isRememberedDevice() {
  try { return !!window.localStorage.getItem(TOKEN_KEY); }
  catch { return false; }
}

// -------------------------------------------------------------------------
// Cross-tab auth sync (Dave 2026-02-19).
//
// PROBLEM: two tabs open on the same origin share one HttpOnly cookie AND one
// localStorage bucket. If Tab B signs in as a different user (or the invite
// force-logout kicks the current one), the cookie + token get REPLACED
// globally — but Tab A's React state still remembers the old identity. Any
// action in Tab A now silently writes as whoever's in the cookie, not the
// person the UI claims. Audit-trail poison + wrong-user writes.
//
// FIX: broadcast `login` / `logout` events on a `BroadcastChannel`. Every
// tab listens; on any remote event that changes identity, the local tab
// force-drops its state and shows LoginScreen. A `_remoteAuthEvent` flag
// prevents the local handler from re-broadcasting an event we just received
// (would infinite-loop otherwise).
//
// One identity per browser at a time. Users with multiple uReserve accounts
// can open a second one in an incognito window.
// -------------------------------------------------------------------------
const AUTH_CHANNEL_NAME = "ureserve-auth-v1";
const _authChannel =
  typeof window !== "undefined" && typeof window.BroadcastChannel === "function"
    ? new window.BroadcastChannel(AUTH_CHANNEL_NAME)
    : null;
let _remoteAuthEvent = false;

function broadcastAuth(msg) {
  if (!_authChannel) return;
  if (_remoteAuthEvent) return;
  try { _authChannel.postMessage(msg); } catch { /* channel closed */ }
}


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = loading
  const [error, setError] = useState("");
  const [tokenExp, setTokenExp] = useState(() => tokenExpiryMs(readToken()));
  const [tenants, setTenants] = useState([]);
  // Persisted across page reloads so switching survives navigation.
  const [currentTenantId, setCurrentTenantIdState] = useState(() => {
    try { return window.sessionStorage.getItem("ureserve.tenantId"); }
    catch { return null; }
  });

  const setCurrentTenantId = useCallback((tid) => {
    setCurrentTenantIdState(tid);
    try {
      if (tid) window.sessionStorage.setItem("ureserve.tenantId", tid);
      else window.sessionStorage.removeItem("ureserve.tenantId");
    } catch { /* ignore */ }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/tenants`);
      if (!res.ok) { setTenants([]); return []; }
      const list = await res.json();
      setTenants(list);
      // Default to the first tenant if no selection persisted or persisted
      // id no longer exists in the user's accessible set.
      setCurrentTenantIdState((cur) => {
        if (cur && list.some((t) => t.tenant_id === cur)) return cur;
        const first = list[0]?.tenant_id || null;
        try {
          if (first) window.sessionStorage.setItem("ureserve.tenantId", first);
          else window.sessionStorage.removeItem("ureserve.tenantId");
        } catch { /* ignore */ }
        return first;
      });
      return list;
    } catch {
      setTenants([]);
      return [];
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      // 2026-02-27 (Dave · perf A + C) — /auth/me, /api/tenants, and
      // /api/associations are all independent — fire them in parallel
      // to save two round-trips on every login/refresh. The
      // associations response goes into a module-level prefetch cache
      // that Shell picks up on first render.
      //
      // 2026-03-04 (Dave · perf D) — Decoupled the setUser call from
      // /tenants. Previously `Promise.all([me, tenants])` meant
      // `setUser` waited for BOTH to complete, so a slow /tenants
      // response held up the entire post-login handoff (Shell mount →
      // /associations/{aid} → /calculate → Dashboard). /tenants is
      // ONLY consumed by the User pill's cross-firm menu and the
      // Admin screens — Dashboard rendering doesn't touch it. Now
      // /auth/me and /tenants run concurrently, but setUser fires
      // the instant /auth/me lands; /tenants populates the pill
      // milliseconds later regardless.
      primeAssociationsPrefetch(readToken());
      const mePromise = authFetch(`${API}/api/auth/me`);
      const tenantsPromise = loadTenants(); // populates its own setState
      const meRes = await mePromise;
      if (meRes.ok) {
        setUser(await meRes.json());
      } else {
        if (meRes.status === 401 && readToken()) {
          setError(SESSION_EXPIRED_MSG);
          try { window.location.hash = "login"; } catch { /* ignore */ }
        }
        writeToken("");
        setTokenExp(null);
        applyAxiosAuth("");
        setUser(false);
        setTenants([]);
        associationsPrefetch.promise = null;
      }
      // Await /tenants only to catch its failure for the catch block —
      // its result was already applied via setTenants inside loadTenants.
      await tenantsPromise;
    } catch {
      setUser(false);
      setTenants([]);
      associationsPrefetch.promise = null;
    }
  }, [loadTenants]);

  useEffect(() => { refresh(); }, [refresh]);

  // 2026-02-19 · cross-tab auth sync listener. Fires whenever ANOTHER tab
  // broadcasts a login/logout on this origin. Uses the module-level
  // `_remoteAuthEvent` flag so the local state changes we trigger here
  // don't feedback-broadcast (would infinite-loop).
  //
  // On remote `login` with a different user_id → drop local state to
  //   LoginScreen. The user must click "Sign in" here if they want in;
  //   we DON'T auto-adopt the new identity because the mental model is
  //   "one identity per browser at a time".
  // On remote `logout` → drop local state to LoginScreen.
  useEffect(() => {
    if (!_authChannel) return undefined;
    const onMsg = (ev) => {
      const msg = ev?.data || {};
      if (msg.type === "login") {
        // Ignore our own identity or the very first login before user
        // is populated on this tab.
        const myId = user && user.user_id;
        if (myId && msg.user_id && msg.user_id === myId) return;
        _remoteAuthEvent = true;
        try {
          writeToken("");
          applyAxiosAuth("");
          setUser(false);
          setTenants([]);
          setCurrentTenantIdState(null);
          clearMethodsCache();
        } finally {
          _remoteAuthEvent = false;
        }
      } else if (msg.type === "logout") {
        _remoteAuthEvent = true;
        try {
          writeToken("");
          applyAxiosAuth("");
          setUser(false);
          setTenants([]);
          setCurrentTenantIdState(null);
          clearMethodsCache();
        } finally {
          _remoteAuthEvent = false;
        }
      }
    };
    _authChannel.addEventListener("message", onMsg);
    return () => _authChannel.removeEventListener("message", onMsg);
  }, [user]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    setError("");
    const res = await authFetch(`${API}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, remember_me: !!rememberMe }),
    });
    if (!res.ok) {
      let msg = "Sign-in failed.";
      try {
        const j = await res.json();
        if (typeof j.detail === "string") msg = j.detail;
      } catch { /* ignore */ }
      setError(msg);
      return false;
    }
    const data = await res.json();
    // Step 7.25 (2026-08-02) — persist token in localStorage when
    // "Keep me logged in" was checked; use sessionStorage otherwise so
    // the token dies when the tab closes.
    if (data.access_token) {
      writeToken(data.access_token, !!rememberMe);
      applyAxiosAuth(data.access_token);
    }
    setTokenExp(tokenExpiryMs(readToken(), true));
    setUser(data);
    // 2026-02-19 · cross-tab sync — tell any OTHER tabs on this origin
    // that identity has changed. They'll compare and force-drop their
    // stale state if the new user_id differs from theirs.
    broadcastAuth({ type: "login", user_id: data.user_id });
    // Step 1 (2026-02-25) — the /auth/login payload is intentionally lean
    // (no `identities` list). Fetch /auth/me so the user menu and any other
    // callers see the full profile (linked portal identities, sop_ack, etc.)
    // immediately after login without waiting for a page reload.
    //
    // 2026-02-27 (Dave · perf A + C) — /auth/me, /api/tenants, and
    // /api/associations run in parallel; independent endpoints, saves
    // two round-trips off login. Associations lands in the module-
    // level prefetch cache that Shell picks up on first render.
    try {
      primeAssociationsPrefetch(data.access_token || readToken());
      const mePromise = authFetch(`${API}/api/auth/me`);
      const tenantsPromise = loadTenants();
      const meRes = await mePromise;
      if (meRes.ok) setUser(await meRes.json());
      // /tenants populates via its own setState — no need to block on it.
      // Kept awaited (fire-and-forget with error-swallow) so a rejection
      // doesn't leak as an unhandled promise.
      tenantsPromise.catch(() => {});
    } catch { /* non-fatal — user still logged in */ }
    return true;
  }, [loadTenants]);

  const logout = useCallback(async () => {
    try {
      await authFetch(`${API}/api/auth/logout`, { method: "POST" });
    } finally {
      writeToken("");
      applyAxiosAuth("");
      setTokenExp(null);
      setUser(false);
      setTenants([]);
      setCurrentTenantId(null);
      // 2026-02-16 (perf) — flush the /methods preload cache so a
      // subsequent login as a different user can't pull the prior
      // user's Contributions data out of memory.
      clearMethodsCache();
      // 2026-02-27 (Dave · perf C) — drop the associations prefetch so
      // the next sign-in (possibly as a different user) doesn't reuse
      // this user's list.
      associationsPrefetch.promise = null;
      associationsPrefetch.at = 0;
      // 2026-03-02 (Dave) — drop the last-visited aid + all per-association
      // Go-To keys so the next sign-in (possibly a different user on the
      // same browser) doesn't inherit this user's session pointers.
      try {
        window.localStorage.removeItem("ureserve.lastAid");
        const keys = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith("ureserve.goto.")) keys.push(k);
        }
        keys.forEach((k) => window.localStorage.removeItem(k));
      } catch { /* private-mode → non-blocking */ }
      // 2026-02-19 · cross-tab sync — tell any other tabs on this origin
      // to drop their state too. Cookie is already cleared globally by
      // /auth/logout; broadcast makes the UI catch up in real time
      // rather than waiting for the next API 401.
      broadcastAuth({ type: "logout" });
      // 2026-08-03 — clear the URL hash so the next login lands on
      // Dashboard by default, regardless of which screen the departing
      // user was on. Bookmarked deep-links (#audit, #firms, etc.) still
      // work because those URLs are opened fresh and never traverse
      // this logout path.
      //
      // 2026-02-19 (Dave) — EXCEPTION: preserve `#login?invite=<email>`
      // so a stale-session-forced logout (triggered when the recipient
      // of an invite clicks the email link while already signed in as
      // someone else) can hand the invite email through to LoginScreen
      // for pre-fill + banner.
      try {
        const h = window.location.hash || "";
        const isInviteLink = h.startsWith("#login") && h.includes("invite=");
        const isResetLink = h.startsWith("#reset") && h.includes("token=");
        if (h && !isInviteLink && !isResetLink) {
          window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}`
          );
        }
      } catch { /* ignore in non-browser environments */ }
    }
  }, [setCurrentTenantId]);

  // 2026-10-10 (Dave) — Expired / revoked sign-in → clean sign-out to the
  // login screen with a clear message (instead of raw "Token expired" in panels).
  const userRef = React.useRef(user);
  userRef.current = user;
  const expiringRef = React.useRef(false);
  const expireSession = useCallback(async () => {
    if (expiringRef.current || !userRef.current) return;
    expiringRef.current = true;
    try {
      await logout();
    } finally {
      setError(SESSION_EXPIRED_MSG);
      try { window.location.hash = "login"; } catch { /* ignore */ }
      expiringRef.current = false;
    }
  }, [logout]);

  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const url = String(err?.config?.url || "");
      if (
        status === 401 &&
        DEAD_SESSION_DETAILS.has(detail) &&
        !url.includes("/auth/login") &&
        !url.includes("/auth/logout")
      ) {
        expireSession();
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, [expireSession]);

  // Step 6.5 (2026-08-02) — Statement of Purpose gate.
  // Called by the blocking modal on first-ever login. Optimistically
  // patches the local user object so the modal unmounts immediately,
  // then confirms with the server (source of truth).
  const acknowledgeSop = useCallback(async () => {
    const res = await authFetch(`${API}/api/users/me/acknowledge-sop`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error(`Acknowledge failed (${res.status})`);
    const data = await res.json();
    setUser((prev) => (prev ? { ...prev, sop_acknowledged_at: data.sop_acknowledged_at } : prev));
    return data;
  }, []);

  // Step 6.75 (2026-02-15) — Force password change gate.
  // Called by the blocking Change-Password modal when a user with the
  // `must_change_password` flag rotates their temporary password. Same
  // optimistic pattern as acknowledgeSop above: clear the flag on the
  // local user object so the modal unmounts, then rely on the server's
  // return payload as the source of truth. Throws a plain Error with
  // the server's `detail` message so the modal can display it verbatim.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await authFetch(`${API}/api/users/me/change-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    if (!res.ok) {
      let msg = `Password change failed (${res.status})`;
      try {
        const j = await res.json();
        if (typeof j.detail === "string") msg = j.detail;
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    const data = await res.json();
    // Step 7.6 (2026-02-15) — Server bumped `token_version` and issued a
    // fresh JWT. Swap it into the persisted token store so subsequent
    // requests from THIS device stay authenticated (any OTHER active
    // session gets 401 on its next call).
    if (data.access_token) {
      const persistent = !!window.localStorage.getItem(TOKEN_KEY);
      writeToken(data.access_token, persistent);
      applyAxiosAuth(data.access_token);
      setTokenExp(tokenExpiryMs(data.access_token, true));
    }
    setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
    return data;
  }, []);

  return (
    <AuthCtx.Provider value={{
      user, error, login, logout, refresh,
      tokenExp, expireSession,
      tenants, currentTenantId, setCurrentTenantId,
      // Slice 5g (2026-02-24) — allow child screens (e.g. FirmsScreen after
      // a firm rename) to refresh the tenants list on demand so the TopBar
      // chip + Dashboard picker labels update without a page reload.
      refreshTenants: loadTenants,
      // Step 6.5 (2026-08-02) — Statement of Purpose gate.
      acknowledgeSop,
      // Step 6.75 (2026-02-15) — Force password change gate.
      changePassword,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// Return the most-privileged role the user holds across all memberships.
// Order: administrator > analyst > view_only.
export function highestRole(user) {
  if (!user || !user.memberships) return null;
  const order = { administrator: 3, analyst: 2, view_only: 1 };
  let best = null;
  let bestScore = 0;
  for (const m of user.memberships) {
    const s = order[m.role] || 0;
    if (s > bestScore) { best = m.role; bestScore = s; }
  }
  return best;
}

// Role-colored dot config used by TopBar + wherever we show identity.
// navy = admin (full power) · sky = analyst (edit) · slate = view-only (read).
export const ROLE_STYLE = {
  administrator: { label: "Administrator", dot: "#EA6A47", ring: "ring-navy" },
  analyst:       { label: "Analyst",       dot: "#3FA9D4", ring: "ring-[#3FA9D4]" },
  view_only:     { label: "View Only",     dot: "#7B8797", ring: "ring-slate" },
};

// -------------------------------------------------------------------------
// Permission predicates — single source of truth for role-based UI gating.
// Policy (Slice 3, per Dave 2026-02-15):
//   * All users see all core screens.
//   * View-only can move sliders freely but cannot Save scenarios (Q1a).
//   * Analysts can edit associations + elements but not create/delete
//     associations (Q2b). Delete of elements is admin-only for safety.
//   * Support content CRUD (FAQ/Glossary/Tenants) is Administrator only (Q3a).
// -------------------------------------------------------------------------
export function isAdmin(user) {
  return highestRole(user) === "administrator";
}

// Slice 4 gate — Platform Administrator = a membership with
// scope_type='platform' AND role='administrator'. A regular tenant admin
// does NOT qualify for cross-firm management screens.
export function isPlatformAdmin(user) {
  if (!user || !Array.isArray(user.memberships)) return false;
  return user.memberships.some(
    (m) => m?.scope_type === "platform" && m?.role === "administrator",
  );
}

// Slice E5n (2026-02-09) — used by the Admin tab dropdown to decide
// which sub-items appear. Firm Admin = any tenant-scoped administrator
// membership. Platform Admins qualify implicitly.
export function isPlatformOrFirmAdmin(user) {
  if (!user || !Array.isArray(user.memberships)) return false;
  if (isPlatformAdmin(user)) return true;
  return user.memberships.some(
    (m) => m?.scope_type === "tenant" && m?.role === "administrator",
  );
}

// Association IDs where the user is an Association Administrator (client-scope admin).
export function assocAdminAids(user) {
  return new Set(
    (user?.memberships || [])
      .filter((m) => m?.scope_type === "client" && m?.role === "administrator" && m?.scope_id)
      .map((m) => String(m.scope_id)),
  );
}

// Platform, Firm, or Association Admin — anyone who may open Admin → Users.
export function isAnyUserAdmin(user) {
  return isPlatformOrFirmAdmin(user) || assocAdminAids(user).size > 0;
}

export function isAnalystOrAdmin(user) {
  const r = highestRole(user);
  return r === "administrator" || r === "analyst";
}

export function isViewOnly(user) {
  return highestRole(user) === "view_only";
}

// True when the user may hit any "Save" / "Add" / "Edit" surface at all.
// View-only users get this as false everywhere.
export function canEdit(user) {
  return isAnalystOrAdmin(user);
}

// Support content, tenants, users — administrator-only content management.
export function canManageContent(user) {
  return isAdmin(user);
}

// 2026-08-04 (Dave §4b · inline association editing) — a user can edit
// the three core Association fields (Plan Year, Members, Opening Fund)
// if they are Platform Admin (unrestricted) OR Firm Admin on the specific
// firm that owns this association. Analysts and View-Only never qualify.
// Mirrors the backend `_require_platform_or_firm_admin` guard.
//
// Step 10.5 (2026-02-16) — extended so an Association Admin on this
// specific `aid` also passes. Mirrors backend
// `_require_association_edit`.
export function canEditAssociation(user, tenantId, aid = null) {
  if (isPlatformAdmin(user)) return true;
  if (!user || !Array.isArray(user.memberships)) return false;
  const tid = String(tenantId || "");
  const aidS = String(aid || "");
  return user.memberships.some((m) => {
    if (
      m?.scope_type === "tenant" &&
      String(m?.scope_id) === tid &&
      m?.role === "administrator"
    ) {
      return true;
    }
    if (
      m?.scope_type === "client" &&
      String(m?.scope_id) === aidS &&
      m?.role === "administrator"
    ) {
      return true;
    }
    return false;
  });
}

// Step 10.5 (2026-02-16) — True iff the user holds an Association Admin
// membership on this specific `aid`. Used by the Association screen to
// show the "Members" and "New Plan Year" self-service surfaces even
// when the user isn't a firm-wide admin.
export function isAssociationAdminFor(user, aid) {
  if (!user || !Array.isArray(user.memberships) || !aid) return false;
  const aidS = String(aid);
  return user.memberships.some(
    (m) =>
      m?.scope_type === "client" &&
      String(m?.scope_id) === aidS &&
      m?.role === "administrator",
  );
}
